export type PromptIssueType = 'duplicate' | 'conflict' | 'weak' | 'illogical' | 'valid';

export type PromptIssue = {
  id: string;
  type: PromptIssueType;
  message: string;
  reason?: string;
  severity?: 'low' | 'medium' | 'high';
  start: number;
  end: number;
  location?: {
    start: number;
    end: number;
  };
  suggestedFix?: string;
  fix?: {
    replacement: string;
  };
};

const cleanPromptText = (value: string): string => value
  .replace(/\s+,/g, ',')
  .replace(/,\s*,/g, ', ')
  .replace(/\s{2,}/g, ' ')
  .replace(/\s+\./g, '.')
  .trim();

const WEAK_PHRASES = [
  'nice image', 'good quality', 'beautiful scene', 'amazing shot',
  'cool look', 'great photo', 'awesome picture', 'perfect shot',
  'pretty scene', 'lovely image', 'stunning view', 'gorgeous',
];

const DUPLICATE_SPLIT_REGEX = /\s*,\s*/;

const REPETITION_TARGETS: Array<{ regex: RegExp; label: string }> = [
  { regex: /\b\d{2,3}mm\s+lens\b/gi, label: 'Lens declaration repeated.' },
  { regex: /\b(?:rule of thirds|centered composition|symmetrical framing)\b/gi, label: 'Composition directive repeated.' },
  { regex: /\b(?:cinematic lighting|low key lighting|natural daylight|soft ambient light)\b/gi, label: 'Lighting directive repeated.' },
  { regex: /\b(?:photorealistic|hyper.?realistic|ultra.?realistic)\b/gi, label: 'Realism directive repeated.' },
  { regex: /\b(?:4k|8k|ultra.?hd)\b/gi, label: 'Resolution directive repeated.' },
  { regex: /\b(?:bokeh|shallow depth of field|shallow dof)\b/gi, label: 'Depth of field directive repeated.' },
  { regex: /\b(?:golden hour|blue hour|magic hour)\b/gi, label: 'Time-of-day lighting repeated.' },
  { regex: /\b(?:film grain|organic grain|vintage grain)\b/gi, label: 'Film grain directive repeated.' },
  { regex: /\b(?:god rays|volumetric light|volumetric rays)\b/gi, label: 'Volumetric light directive repeated.' },
  { regex: /\b(?:motion blur|camera blur)\b/gi, label: 'Motion blur directive repeated.' },
];

// ─── Enhanced semantic conflict detection (AI-like without API) ───
const SEMANTIC_CONFLICTS: Array<{ 
  patterns: RegExp[]; 
  message: string; 
  fix: string;
  category: 'shot' | 'lighting' | 'environment' | 'mood' | 'style' | 'time';
}> = [
  // Shot scale semantic conflicts
  {
    patterns: [/\b(?:extreme\s+)?close[\s-]?up\b/i, /\b(?:full\s+body|wide\s+shot|establishing)\b/i],
    message: 'Shot scale conflict: close framing vs wide framing.',
    fix: 'medium close-up shot',
    category: 'shot'
  },
  {
    patterns: [/\b(?:tight\s+portrait|headshot)\b/i, /\b(?:wide\s+angle|panorama|landscape)\b/i],
    message: 'Shot scale conflict: tight portrait vs wide landscape.',
    fix: 'medium portrait framing',
    category: 'shot'
  },
  // Lighting semantic conflicts
  {
    patterns: [/\b(?:warm|golden|amber|sunset)\b/i, /\b(?:cool|cold|blue|icy|moonlight)\b/i],
    message: 'Color temperature conflict: warm vs cool tones.',
    fix: 'warm golden lighting',
    category: 'lighting'
  },
  {
    patterns: [/\b(?:bright|sunny|daylight|noon)\b/i, /\b(?:dark|night|midnight|shadow)\b/i],
    message: 'Exposure conflict: bright vs dark lighting.',
    fix: 'balanced daylight',
    category: 'lighting'
  },
  // Environment semantic conflicts
  {
    patterns: [/\b(?:indoor|interior|room|studio)\b/i, /\b(?:outdoor|exterior|nature|landscape)\b/i],
    message: 'Setting conflict: indoor vs outdoor environment.',
    fix: 'controlled indoor environment',
    category: 'environment'
  },
  {
    patterns: [/\b(?:urban|city|metropolitan|street)\b/i, /\b(?:nature|forest|wilderness|mountain)\b/i],
    message: 'Location conflict: urban vs natural setting.',
    fix: 'urban environment',
    category: 'environment'
  },
  // Mood semantic conflicts
  {
    patterns: [/\b(?:peaceful|calm|serene|tranquil)\b/i, /\b(?:chaotic|intense|dramatic|violent)\b/i],
    message: 'Mood conflict: peaceful vs intense atmosphere.',
    fix: 'balanced atmospheric mood',
    category: 'mood'
  },
  // Time semantic conflicts
  {
    patterns: [/\b(?:dawn|sunrise|morning)\b/i, /\b(?:dusk|sunset|evening|night)\b/i],
    message: 'Time conflict: dawn/day vs dusk/night.',
    fix: 'golden hour lighting',
    category: 'time'
  },
  // Style semantic conflicts
  {
    patterns: [/\b(?:minimalist|simple|clean)\b/i, /\b(?:ornate|complex|detailed|baroque)\b/i],
    message: 'Style conflict: minimalist vs ornate.',
    fix: 'clean balanced composition',
    category: 'style'
  },
];

// ─── Missing essential elements detection ───
const ESSENTIAL_ELEMENTS = {
  subject: /\b(?:subject|character|person|figure|man|woman|girl|boy|hero|protagonist|model)\b/i,
  action: /\b(?:standing|sitting|walking|running|jumping|looking|holding|posing|gesturing|expressing)\b/i,
  environment: /\b(?:background|setting|scene|location|environment|backdrop|surroundings)\b/i,
  lighting: /\b(?:light|lighting|lit|illuminated|shadow|glow|highlight)\b/i,
  camera: /\b(?:camera|shot|angle|perspective|lens|focal|framing|view|composition)\b/i,
  mood: /\b(?:mood|atmosphere|tone|feeling|emotion|ambiance|vibe)\b/i,
  quality: /\b(?:quality|detail|resolution|sharp|clear|crisp|high-def|professional)\b/i,
};

// ─── Semantic duplicate detection (detects similar meanings) ───
const SEMANTIC_DUPLICATES: Array<{ 
  group: string[]; 
  message: string;
}> = [
  {
    group: ['cinematic', 'filmic', 'movie-like', 'film look', 'cinematic style'],
    message: 'Semantic duplicate: multiple cinematic descriptors.'
  },
  {
    group: ['beautiful', 'gorgeous', 'stunning', 'pretty', 'lovely', 'attractive'],
    message: 'Semantic duplicate: multiple beauty descriptors.'
  },
  {
    group: ['detailed', 'intricate', 'complex', 'elaborate', 'fine details'],
    message: 'Semantic duplicate: multiple detail descriptors.'
  },
  {
    group: ['realistic', 'lifelike', 'true-to-life', 'authentic', 'genuine'],
    message: 'Semantic duplicate: multiple realism descriptors.'
  },
  {
    group: ['dramatic', 'theatrical', 'intense', 'striking', 'powerful'],
    message: 'Semantic duplicate: multiple drama descriptors.'
  },
];

// ─── Contradiction pairs: if both exist in the prompt, they conflict ───
interface ConflictPair {
  a: RegExp;
  b: RegExp;
  message: string;
  fix: string;
}

const CONFLICT_PAIRS: ConflictPair[] = [
  // Camera angle conflicts
  { a: /\beye[\s-]?level\b/i, b: /\b(?:overhead|top[\s-]?down|bird'?s[\s-]?eye)\b/i, message: 'Camera angle conflict: eye-level vs overhead.', fix: 'eye-level angle' },
  { a: /\blow[\s-]?angle\b/i, b: /\b(?:overhead|top[\s-]?down|bird'?s[\s-]?eye|high[\s-]?angle)\b/i, message: 'Camera angle conflict: low-angle vs high/overhead.', fix: 'low angle' },
  { a: /\bhigh[\s-]?angle\b/i, b: /\b(?:worm'?s[\s-]?eye|low[\s-]?angle|upward)\b/i, message: 'Camera angle conflict: high-angle vs low-angle.', fix: 'high angle' },

  // Shot scale conflicts
  { a: /\b(?:extreme\s+)?close[\s-]?up\b/i, b: /\b(?:wide\s+shot|full\s+body|full\s+framing|establishing\s+shot|extreme\s+wide)\b/i, message: 'Shot scale conflict: close-up vs wide shot.', fix: 'medium close-up' },
  { a: /\bmacro\b/i, b: /\b(?:wide\s+shot|full\s+body|landscape|panorama)\b/i, message: 'Shot scale conflict: macro vs wide/landscape.', fix: 'macro shot' },

  // Focus/DOF conflicts
  { a: /\b(?:wide\s+aperture|f\/?1\.\d|shallow\s+(?:depth|dof|focus))\b/i, b: /\b(?:deep\s+focus|everything\s+in\s+focus|infinite\s+focus)\b/i, message: 'Focus conflict: shallow DOF vs deep focus.', fix: 'shallow depth of field' },
  { a: /\b(?:tack\s+sharp|razor\s+sharp)\b/i, b: /\b(?:soft\s+focus|dreamy\s+blur|heavy\s+bokeh)\b/i, message: 'Focus conflict: tack sharp vs soft focus.', fix: 'tack sharp' },

  // Lighting temperature conflicts
  { a: /\b(?:warm\s+(?:light|tone|glow)|golden|amber|orange\s+light)\b/i, b: /\b(?:cold\s+(?:light|tone)|cool\s+(?:light|tone)|blue\s+light|icy)\b/i, message: 'Lighting temperature conflict: warm vs cold tones.', fix: 'warm golden lighting' },

  // Time of day conflicts
  { a: /\b(?:night|midnight|dark\s+sky|moonlight|starlight)\b/i, b: /\b(?:bright\s+sun|sunny|daylight|noon|midday|sunlight)\b/i, message: 'Time conflict: night scene with daylight.', fix: 'moonlit night scene' },
  { a: /\b(?:golden\s+hour|sunset|sunrise)\b/i, b: /\b(?:midnight|deep\s+night|pitch\s+black)\b/i, message: 'Time conflict: golden hour vs midnight.', fix: 'golden hour' },
  { a: /\b(?:dawn|sunrise|morning)\b/i, b: /\b(?:dusk|sunset|evening|twilight)\b/i, message: 'Time conflict: dawn vs dusk.', fix: 'dawn light' },

  // Environment conflicts
  { a: /\b(?:indoor|interior|inside|room)\b/i, b: /\b(?:outdoor|exterior|open\s+air|landscape|sky)\b/i, message: 'Environment conflict: indoor vs outdoor.', fix: 'indoor setting' },
  { a: /\b(?:urban|city|metropolitan|downtown|skyscraper)\b/i, b: /\b(?:rural|countryside|wilderness|forest|jungle)\b/i, message: 'Environment conflict: urban vs rural.', fix: 'urban environment' },
  { a: /\b(?:underwater|submerged|deep\s+sea)\b/i, b: /\b(?:desert|arid|dry|sandy\s+dunes)\b/i, message: 'Environment conflict: underwater vs desert.', fix: 'underwater scene' },

  // Weather conflicts
  { a: /\b(?:sunny|clear\s+sky|bright\s+day)\b/i, b: /\b(?:rainy|storm|thunder|overcast|cloudy|downpour)\b/i, message: 'Weather conflict: sunny vs rainy/stormy.', fix: 'clear weather' },
  { a: /\b(?:snow|blizzard|winter|frozen)\b/i, b: /\b(?:tropical|hot|desert\s+heat|summer\s+sun)\b/i, message: 'Weather conflict: snow/winter vs tropical/hot.', fix: 'snowy winter' },

  // Mood/atmosphere conflicts
  { a: /\b(?:peaceful|serene|calm|tranquil)\b/i, b: /\b(?:chaotic|violent|intense|dramatic|explosive)\b/i, message: 'Mood conflict: peaceful vs chaotic.', fix: 'peaceful atmosphere' },
  { a: /\b(?:happy|joyful|cheerful|upbeat)\b/i, b: /\b(?:sad|melancholy|somber|depressed|gloomy)\b/i, message: 'Mood conflict: happy vs sad.', fix: 'joyful mood' },
  { a: /\b(?:horror|terrifying|scary|creepy)\b/i, b: /\b(?:whimsical|playful|cute|adorable)\b/i, message: 'Mood conflict: horror vs whimsical.', fix: 'dark atmospheric mood' },

  // Style conflicts
  { a: /\b(?:minimalist|minimal|clean|simple)\b/i, b: /\b(?:maximalist|ornate|complex|baroque|cluttered)\b/i, message: 'Style conflict: minimalist vs maximalist.', fix: 'minimalist style' },
  { a: /\b(?:vintage|retro|old\s+school|film\s+look)\b/i, b: /\b(?:futuristic|sci[\s-]?fi|cyberpunk|neon)\b/i, message: 'Style conflict: vintage vs futuristic.', fix: 'vintage aesthetic' },
  { a: /\b(?:black\s+and\s+white|monochrome|grayscale)\b/i, b: /\b(?:vibrant\s+colors|colorful|saturated|teal\s+and\s+orange)\b/i, message: 'Color conflict: black & white vs vibrant colors.', fix: 'black and white' },
  { a: /\b(?:rich\s+(?:saturated\s+)?tones?|rich\s+colors?|high\s+saturation)\b/i, b: /\b(?:desaturated|muted\s+tones?|low\s+saturation|washed\s+out)\b/i, message: 'Color conflict: rich/saturated tones vs desaturated tones.', fix: 'balanced muted tones' },

  // Render style conflicts
  { a: /\b(?:photorealistic|hyper[\s-]?realistic|real\s+photo)\b/i, b: /\b(?:cartoon|anime|illustration|sketch|drawing|cel[\s-]?shaded)\b/i, message: 'Render conflict: photorealistic vs cartoon/anime.', fix: 'photorealistic rendering' },
  { a: /\b(?:oil\s+painting|watercolor|acrylic)\b/i, b: /\b(?:3d\s+render|unreal\s+engine|octane\s+render)\b/i, message: 'Render conflict: traditional painting vs 3D render.', fix: 'oil painting style' },

  // VFX conflicts
  { a: /\b(?:clean|noise[\s-]?free|pristine)\b/i, b: /\b(?:film\s+grain|grainy|noisy|gritty)\b/i, message: 'VFX conflict: clean image vs film grain.', fix: 'clean noise-free' },
  { a: /\b(?:sharp\s+detail|razor\s+sharp|crisp)\b/i, b: /\b(?:heavy\s+bloom|glow|soft\s+blur|dreamy)\b/i, message: 'VFX conflict: sharp detail vs heavy bloom/blur.', fix: 'sharp detailed' },
  { a: /\b(?:slow[\s-]?motion|freeze\s+frame|frozen\s+moment)\b/i, b: /\b(?:heavy\s+motion\s+blur|speed\s+blur|motion\s+trails)\b/i, message: 'VFX conflict: slow motion vs heavy motion blur.', fix: 'slow motion capture' },

  // Composition conflicts
  { a: /\b(?:rule\s+of\s+thirds)\b/i, b: /\b(?:centered|dead\s+center|symmetrical)\b/i, message: 'Composition conflict: rule of thirds vs centered.', fix: 'rule of thirds composition' },
  { a: /\b(?:negative\s+space|minimal\s+elements)\b/i, b: /\b(?:crowded|busy|dense|cluttered)\b/i, message: 'Composition conflict: negative space vs crowded.', fix: 'generous negative space' },

  // Skin/retouch conflicts
  { a: /\b(?:silhouette|backlit\s+shadow|dark\s+outline)\b/i, b: /\b(?:skin\s+smoothing|skin\s+detail|pore\s+detail|beauty\s+retouch)\b/i, message: 'Retouch conflict: silhouette makes skin retouch invisible.', fix: 'silhouette composition' },

  // Aspect ratio / framing text conflicts
  { a: /\b(?:vertical|portrait\s+orientation|9:16|tall\s+frame)\b/i, b: /\b(?:horizontal|landscape\s+orientation|16:9|wide\s+frame|ultrawide)\b/i, message: 'Framing conflict: vertical vs horizontal orientation.', fix: 'widescreen framing' },
];

const findAllRanges = (text: string, needle: string): Array<{ start: number; end: number }> => {
  if (!needle) return [];

  const ranges: Array<{ start: number; end: number }> = [];
  let cursor = 0;
  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();

  while (cursor < text.length) {
    const index = lowerText.indexOf(lowerNeedle, cursor);
    if (index === -1) break;
    ranges.push({ start: index, end: index + needle.length });
    cursor = index + needle.length;
  }

  return ranges;
};

export const analyzeInlinePromptIssues = (prompt: string): PromptIssue[] => {
  const normalized = prompt.trim();
  if (!normalized) return [];

  const issues: PromptIssue[] = [];

  // ─── Duplicate segment detection ───
  const segments = normalized.split(DUPLICATE_SPLIT_REGEX).map((part) => part.trim()).filter(Boolean);
  const seen = new Set<string>();

  segments.forEach((segment) => {
    const key = segment.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      return;
    }

    const range = findAllRanges(prompt, segment).slice(1, 2)[0];
    if (!range) return;

    issues.push({
      id: `duplicate-${range.start}-${range.end}`,
      type: 'duplicate',
      message: `Duplicate: "${segment}" appears more than once.`,
      reason: 'The same segment appears more than once in the same prompt beat.',
      severity: 'medium',
      start: range.start,
      end: range.end,
      location: { start: range.start, end: range.end },
      suggestedFix: 'Remove the duplicate.',
      fix: { replacement: '' },
    });
  });

  // ─── Contradiction pair detection ───
  for (const pair of CONFLICT_PAIRS) {
    const matchA = pair.a.exec(prompt);
    const matchB = pair.b.exec(prompt);
    if (matchA && matchB) {
      // Mark the second match as the conflict
      const start = matchB.index;
      const end = matchB.index + matchB[0].length;
      issues.push({
        id: `conflict-${start}-${end}`,
        type: 'conflict',
        message: pair.message,
        reason: 'These two directives contradict each other in the same prompt.',
        severity: 'high',
        start,
        end,
        location: { start, end },
        suggestedFix: pair.fix,
        fix: { replacement: '' },
      });
    }
  }

  // ─── Weak phrase detection ───
  WEAK_PHRASES.forEach((phrase) => {
    findAllRanges(prompt, phrase).forEach((range) => {
      issues.push({
        id: `weak-${range.start}-${range.end}`,
        type: 'weak',
        message: `Weak phrase: "${phrase}". Use specific cinematic detail instead.`,
        reason: 'Phrase is generic and does not provide visual specificity.',
        severity: 'low',
        start: range.start,
        end: range.end,
        location: { start: range.start, end: range.end },
        suggestedFix: 'Replace generic wording with cinematic detail.',
        fix: {
          replacement: 'cinematic composition with precise subject and action',
        },
      });
    });
  });

  // ─── Repetition targets ───
  REPETITION_TARGETS.forEach(({ regex, label }) => {
    const matches = [...prompt.matchAll(regex)];
    if (matches.length < 2) return;
    const duplicate = matches[1];
    const token = duplicate[0] || '';
    const start = duplicate.index ?? -1;
    if (!token || start < 0) return;

    issues.push({
      id: `repeat-${start}-${start + token.length}`,
      type: 'duplicate',
      message: label,
      reason: 'Repeated directive lowers prompt clarity and introduces redundancy.',
      severity: 'medium',
      start,
      end: start + token.length,
      location: { start, end: start + token.length },
      suggestedFix: 'Remove repeated directive and keep one authoritative instruction.',
      fix: { replacement: '' },
    });
  });

  // ─── NEW: Semantic conflict detection (AI-like without API) ───
  for (const conflict of SEMANTIC_CONFLICTS) {
    const matches: Array<{ text: string; index: number }> = [];
    
    for (const pattern of conflict.patterns) {
      const match = pattern.exec(prompt);
      if (match && match.index !== undefined) {
        matches.push({ text: match[0], index: match.index });
      }
    }
    
    // If we found 2+ conflicting patterns
    if (matches.length >= 2) {
      // Mark the second one as the conflict
      const secondMatch = matches[1];
      issues.push({
        id: `semantic-conflict-${secondMatch.index}-${secondMatch.text.length}`,
        type: 'conflict',
        message: conflict.message,
        reason: `Semantic conflict in ${conflict.category}: these concepts don't work well together.`,
        severity: 'high',
        start: secondMatch.index,
        end: secondMatch.index + secondMatch.text.length,
        location: { start: secondMatch.index, end: secondMatch.index + secondMatch.text.length },
        suggestedFix: conflict.fix,
        fix: { replacement: '' },
      });
    }
  }

  // ─── NEW: Semantic duplicate detection ───
  for (const semanticDup of SEMANTIC_DUPLICATES) {
    const found: Array<{ word: string; index: number }> = [];
    
    for (const word of semanticDup.group) {
      const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      const match = regex.exec(prompt);
      if (match && match.index !== undefined) {
        found.push({ word, index: match.index });
      }
    }
    
    // If found 2+ similar words
    if (found.length >= 2) {
      // Mark duplicates after the first one
      for (let i = 1; i < found.length; i++) {
        const dup = found[i];
        issues.push({
          id: `semantic-dup-${dup.index}-${dup.word.length}`,
          type: 'duplicate',
          message: semanticDup.message,
          reason: `Similar meaning word "${dup.word}" detected (group: ${found.map(f => f.word).join(', ')}).`,
          severity: 'low',
          start: dup.index,
          end: dup.index + dup.word.length,
          location: { start: dup.index, end: dup.index + dup.word.length },
          suggestedFix: 'Remove redundant descriptor.',
          fix: { replacement: '' },
        });
      }
    }
  }

  // ─── NEW: Missing essential elements (warnings, not errors) ───
  const missingElements: string[] = [];
  if (!ESSENTIAL_ELEMENTS.subject.test(prompt)) missingElements.push('subject');
  if (!ESSENTIAL_ELEMENTS.action.test(prompt)) missingElements.push('action/pose');
  if (!ESSENTIAL_ELEMENTS.lighting.test(prompt)) missingElements.push('lighting');
  if (!ESSENTIAL_ELEMENTS.camera.test(prompt)) missingElements.push('camera/framing');
  
  if (missingElements.length > 0 && missingElements.length < 4) {
    // Add a single weak issue for missing elements
    issues.push({
      id: `missing-elements-0`,
      type: 'weak',
      message: `Missing: ${missingElements.join(', ')}. Consider adding these for a complete prompt.`,
      reason: 'A strong prompt typically includes subject, action, lighting, and camera details.',
      severity: 'low',
      start: 0,
      end: Math.min(10, prompt.length),
      location: { start: 0, end: Math.min(10, prompt.length) },
      suggestedFix: `Add ${missingElements.join(', ')} details.`,
      fix: { replacement: '' },
    });
  }

  // Deduplicate overlapping issues (keep higher severity)
  const deduped = deduplicateIssues(issues);
  return deduped.sort((a, b) => a.start - b.start);
};

function deduplicateIssues(issues: PromptIssue[]): PromptIssue[] {
  const result: PromptIssue[] = [];
  const sorted = [...issues].sort((a, b) => a.start - b.start);

  for (const issue of sorted) {
    const overlapping = result.find(
      (existing) => issue.start < existing.end && issue.end > existing.start
    );
    if (!overlapping) {
      result.push(issue);
    } else {
      // Keep the higher severity one
      const severityOrder = { high: 3, medium: 2, low: 1 };
      if ((severityOrder[issue.severity || 'low'] || 0) > (severityOrder[overlapping.severity || 'low'] || 0)) {
        const idx = result.indexOf(overlapping);
        result[idx] = issue;
      }
    }
  }

  return result;
}

export const resolveAllPromptIssues = (prompt: string): string => {
  const issues = analyzeInlinePromptIssues(prompt)
    .filter((issue) => issue.fix && issue.end > issue.start)
    .sort((a, b) => b.start - a.start);

  let next = prompt;
  issues.forEach((issue) => {
    const replacement = issue.fix?.replacement ?? '';
    next = `${next.slice(0, issue.start)}${replacement}${next.slice(issue.end)}`;
  });

  return cleanPromptText(next)
    .replace(/\bnice image\b/gi, 'clear visual scene')
    .replace(/\bgood quality\b/gi, 'high quality details')
    .replace(/\bbeautiful scene\b/gi, 'coherent visual scene');
};
